<template>
  <b-container fluid>
    <div class="row">
      <div class="col-lg-12">
        <h2 class="lz-home-heading">{{ title }}</h2>
      </div>
    </div>
    <div class="row" v-if="isAdmin && !loading">
      <div class="col-lg-12">
        <resource-toolbar
          v-model="editMode"
          :admin="isAdmin"
          @manage="OpenManageSections"
          @add="AddResource" />
      </div>
    </div>
    <div class="row" v-if="loading">
      <div class="col-lg-12">
        <b-skeleton width="30%" class="mb-4"></b-skeleton>
        <b-skeleton-img no-aspect height="132px" class="mb-3"></b-skeleton-img>
        <b-skeleton-img no-aspect height="132px"></b-skeleton-img>
      </div>
    </div>
    <div class="row" v-else>
      <div class="col-lg-12">
        <b-tabs pills card class="lz-home-inner-tabs">
          <b-tab
            v-for="tab in tabs.filter((tab) => tab.show || (canEdit && tab.id !== 'tab_search_results'))"
            :key="tab.id"
            :active="tab.active"
            lazy
            @click="tabChanged(tab.id)">
            <template #title>
              <span :class="{ 'is-hidden-resource': !tab.show && tab.id !== 'tab_search_results' }">{{ tab.title }}</span>
            </template>
            <component
              :is="tab.component"
              v-if="tab.component"
              :subSections="canEdit ? tab.subSections : VisibleSubSections(tab.subSections)"
              :isAdmin="canEdit"
              @edit="EditResource"
              @toggle="ToggleResource" />
            <template v-if="tab.id === 'tab_search_results'">
              <div v-if="searchResults.length">
                <div
                  v-for="group in searchResults"
                  :key="group.tabTitle"
                  class="lz-resource-group">
                  <div class="lz-resource-group-head">
                    <span class="lz-resource-group-title">{{ group.tabTitle }}</span>
                  </div>
                  <content-component :subSections="group.subSections" />
                </div>
              </div>
              <div v-else class="text-center lz-search-empty">
                <b-icon-search font-scale="2"></b-icon-search>
                <h3>No results for "{{ searchQuery.trim() }}"</h3>
                <p>Check the spelling or try a different keyword.</p>
              </div>
            </template>
          </b-tab>
          <template #tabs-end>
            <li class="nav-item ml-auto lz-training-search">
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <b-icon-search></b-icon-search>
                </b-input-group-prepend>
                <b-form-input
                  v-model="searchQuery"
                  type="search"
                  placeholder="Search trainings..." />
              </b-input-group>
            </li>
          </template>
        </b-tabs>
      </div>
    </div>
    <resource-form-modal
      ref="resourceModal"
      tab="training"
      :parentOptions="parentOptions"
      @saved="ResourceSaved" />
    <manage-sections-modal
      ref="manageSectionsModal"
      tab="training"
      @changed="fetchResources" />
  </b-container>
</template>

<script>
import Services from '../../../services/main.service.vue'
import contentComponent from './content-component.vue'
import resourceFormModal from '../resource-form-modal.vue'
import manageSectionsModal from '../manage-sections-modal.vue'
import resourceToolbar from '../resource-toolbar.vue'

export default {
  name: 'HomeTrainingModule',
  components: {
    contentComponent,
    resourceFormModal,
    manageSectionsModal,
    resourceToolbar
  },
  props: {
    title: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      editMode: false,
      searchQuery: '',
      lastActiveTabId: '',
      activeSectionUid: null,
      rawSections: [],
      tabs: []
    }
  },
  computed: {
    isAdmin() {
      return this.$store.state.user.hasAdminPrivileges
    },
    canEdit() {
      return this.isAdmin && this.editMode
    },
    parentOptions() {
      const options = []
      const walk = (nodes, depth) => {
        nodes.forEach((node) => {
          if (!node.link && !node.videoLink && !node.filesLink) {
            options.push({
              value: node.uid,
              text: `${'— '.repeat(depth)}${node.title}`
            })
            walk(node.children ?? [], depth + 1)
          }
        })
      }
      walk(this.rawSections, 0)
      return options
    },
    searchResults() {
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) return []
      return this.tabs
        .filter((tab) => tab.id !== 'tab_search_results')
        .map((tab) => ({
          tabTitle: tab.title,
          subSections: tab.subSections.filter((subSection) =>
            subSection.title.toLowerCase().includes(query)
          )
        }))
        .filter((group) => group.subSections.length > 0)
    }
  },
  watch: {
    searchQuery(value) {
      const hasQuery = value.trim().length > 0
      const targetId = hasQuery ? 'tab_search_results' : this.lastActiveTabId
      this.tabs.forEach((tab) => {
        tab.active = tab.id === targetId
      })
      const searchTab = this.tabs.find((tab) => tab.id === 'tab_search_results')
      if (searchTab) {
        searchTab.show = hasQuery
      }
    }
  },
  mounted() {
    this.fetchResources()
  },
  methods: {
    async fetchResources() {
      try {
        this.loading = true
        const { status, data } = await Services.Home.GetHomeResources()
        if (status === 200) {
          const sections = data?.content?.training ?? []
          this.rawSections = sections
          const currentUid = this.activeSectionUid
          const tabs = sections.map((section, index) => ({
            id: `tab_${section.uid}`,
            uid: section.uid,
            component: 'content-component',
            title: section.title,
            active: currentUid ? section.uid === currentUid : index === 0,
            show: section.show !== false,
            subSections: (section.children ?? []).map((child) =>
              this.mapNode(child, section.uid)
            )
          }))
          if (!tabs.some((tab) => tab.active) && tabs.length) {
            tabs[0].active = true
          }
          tabs.push({
            id: 'tab_search_results',
            component: null,
            title: 'Search Results',
            active: false,
            show: false,
            subSections: []
          })
          this.tabs = tabs
          const activeTab = tabs.find((tab) => tab.active)
          this.lastActiveTabId = activeTab?.id ?? ''
          this.activeSectionUid = activeTab?.uid ?? null
        }
      } catch (error) {
        console.error(error.message)
        this.$toasted.error('Failed to load training modules, please try again later.').goAway(3500)
        this.tabs = []
      } finally {
        this.loading = false
      }
    },
    mapNode(node, parentUid = null) {
      const item = {
        uid: node.uid,
        parentUid,
        title: node.title,
        show: node.show,
        sortOrder: node.sortOrder,
        meta: node.meta
      }
      if (node.link) {
        item.link = node.link
      }
      if (node.videoLink) {
        item.videoLink = node.videoLink
      }
      if (node.filesLink) {
        item.filesLink = node.filesLink
      }
      if (node.children?.length) {
        item.subSections = node.children.map((child) =>
          this.mapNode(child, node.uid)
        )
      }
      return item
    },
    VisibleSubSections(items = []) {
      return items
        .filter((itm) => itm.show !== false)
        .map((itm) =>
          itm.subSections
            ? { ...itm, subSections: this.VisibleSubSections(itm.subSections) }
            : itm
        )
    },
    AddResource() {
      if (!this.canEdit) {
        return
      }
      this.$refs.resourceModal.Open(null, { parentUid: this.activeSectionUid })
    },
    OpenManageSections() {
      if (!this.canEdit) {
        return
      }
      const sections = this.tabs.filter((tab) => tab.id !== 'tab_search_results')
      this.$refs.manageSectionsModal.Open(sections)
    },
    EditResource(item) {
      if (!this.canEdit) {
        return
      }
      this.$refs.resourceModal.Open(item)
    },
    async ToggleResource(item) {
      if (!this.canEdit) {
        return
      }
      try {
        const { status, data } = await Services.Home.UpdateHomeResource(item.uid, {
          show: item.show === false
        })
        if (status === 200 && data?.success !== false) {
          this.fetchResources()
        } else {
          this.$toasted.error('Failed to update visibility, please try again later.').goAway(3500)
        }
      } catch (error) {
        console.error(error.message)
        this.$toasted.error('Failed to update visibility, please try again later.').goAway(3500)
      }
    },
    ResourceSaved() {
      this.fetchResources()
    },
    tabMounted() {
      const selectedTab = this.tabs.find((tab) => tab.active)
      if (selectedTab) {
        this.tabChanged(selectedTab.id)
      }
    },
    tabChanged(tabId) {
      if (tabId !== 'tab_search_results') {
        this.lastActiveTabId = tabId
        const tab = this.tabs.find((itm) => itm.id === tabId)
        this.activeSectionUid = tab?.uid ?? this.activeSectionUid
      }
      this.$root.$emit(`${tabId}_selected`)
    }
  }
}
</script>

<style scoped>
/* Shared home styling (toolbar, tabs, cards) lives in src/styles/logs.css. */
.is-hidden-resource {
  opacity: 0.55;
}
.lz-training-search {
  display: flex;
  align-items: center;
  min-width: 240px;
}
.lz-training-search >>> .input-group {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  overflow: hidden;
  background-color: #fff;
}
.lz-training-search >>> .input-group:focus-within {
  border-color: #0073be;
  box-shadow: 0 0 0 3px rgba(0, 115, 190, 0.15);
}
.lz-training-search >>> .input-group-text {
  background-color: transparent;
  border: none;
  color: #94a3b8;
  padding-left: 12px;
}
.lz-training-search >>> .form-control {
  border: none;
  font-size: 13.5px;
  background-color: transparent;
}
.lz-training-search >>> .form-control:focus {
  box-shadow: none;
}
.lz-training-search >>> .form-control::placeholder {
  color: #94a3b8;
}
.lz-search-empty {
  padding: 48px 12px;
  color: #94a3b8;
}
.lz-search-empty h3 {
  font-size: 18px;
  font-weight: 700;
  margin: 14px 0 4px;
  color: #334155;
}
.lz-search-empty p {
  margin: 0;
  font-size: 14px;
}
</style>
